/**
 * Official rules sheets as PNGs under `public/Bulandi Event/` (folder name includes a space).
 * Paths are relative to that directory; keys are `bulandiSubEvents` `id` values.
 * Values are a single path string or an ordered array for multi-page rules (e.g. Clash of Clans).
 */

const ROOT = "/Bulandi Event";

/** @type {Record<string, string | string[]>} */
const EVENT_ID_TO_RELATIVE_PNG = {
  "foot-loose-juniors": "Juniors/Foot Loose.png",
  "wanna-b-picasso-juniors": "Juniors/Wanna-B-Picasso.png",
  "verse-vibes-juniors": "Juniors/Verse Vibes.png",
  "riddle-rush-juniors": "Juniors/Riddle Rush.png",
  "mic-drop-intermediates": "Intermediate/Mic Drop.png",
  "foot-loose-intermediates": "Intermediate/Foot Loose.png",
  "wanna-b-picasso-intermediates": "Intermediate/Wanna-B-Picasso.png",
  "ink-it-intermediates": "Intermediate/Ink It.png",
  "tale-spin-intermediates": "Intermediate/Tale Spin.png",
  "knives-out-intermediates": "Intermediate/Knives Out.png",
  "nitro-rush-intermediates": "Intermediate/Nitro Rush.png",
  "mic-drop-seniors": "Seniors/Mic Drop.png",
  "funk-fusion-seniors": "Seniors/Funk Fusion.png",
  "knives-out-seniors": "Seniors/Knives Out.png",
  "tote-studio-seniors": "Seniors/Tote Studio.png",
  "sher-lock-key-seniors": "Seniors/Sher-Lock & Key.png",
  "profit-or-laugh-seniors": "Seniors/Profit or Laugh.png",
  "ink-it-seniors": "Seniors/Ink It.png",
  "viral-voltage-seniors": "Seniors/Viral Voltage.png",
  "clash-of-clans-family": [
    "Family/Clash of Clans1.png",
    "Family/Clash of Clans2.png",
  ],
};

function encodePublicPath(relativePath) {
  const segments = `${ROOT}/${relativePath}`.split("/").filter(Boolean);
  return `/${segments.map((s) => encodeURIComponent(s)).join("/")}`;
}

/**
 * @param {{ id?: string, name?: string } | null | undefined} event
 * @returns {string[] | null} Encoded URLs for use in `src` / `href`, or null when no asset is mapped.
 */
export function getBulandiEventRulesPngUrls(event) {
  if (!event?.id) return null;
  const rel = EVENT_ID_TO_RELATIVE_PNG[event.id];
  if (!rel) return null;
  const paths = Array.isArray(rel) ? rel : [rel];
  return paths.map(encodePublicPath);
}

/**
 * @param {{ id?: string, name?: string } | null | undefined} event
 * @returns {string | null} First rules page URL, for backward compatibility.
 */
export function getBulandiEventRulesPngUrl(event) {
  const urls = getBulandiEventRulesPngUrls(event);
  return urls?.[0] ?? null;
}

/**
 * @param {{ id?: string, name?: string } | null | undefined} event
 * @returns {boolean}
 */
export function hasBulandiEventRulesPng(event) {
  return Boolean(getBulandiEventRulesPngUrls(event)?.length);
}

/**
 * Suggested filename for the download attribute (ASCII, no path segments).
 * @param {{ name?: string } | null | undefined} event
 * @param {number} [pageIndex=0]
 */
export function getBulandiEventRulesPngDownloadFilename(event, pageIndex = 0) {
  const raw = String(event?.name || "event")
    .trim()
    .replace(/[<>:"/\\|?*]+/g, "")
    .replace(/\s+/g, "-");
  const base = raw.length ? raw : "event";
  const urls = getBulandiEventRulesPngUrls(event);
  const suffix = urls && urls.length > 1 ? `-page-${pageIndex + 1}` : "";
  return `Bulandi-2026-${base}-rules${suffix}.png`;
}
